import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';

import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {AppState, wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import {Chat} from '@components';
import {metaInfo} from '@configs';
import {JOBFEED, SEARCH} from '@constants';
import {loadSEO} from '@helpers';
import usePageLoad from '@hooks/usePageLoad';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  getLocationIds,
  slugToTitle,
  utilServerSideDeviceDetection,
} from '@utils';

import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {getListSEOContent, getPageUrl} from '@utils/seo';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import cookie from 'cookie';
import {gladiatorService} from '@jobseeker/common-core/services';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {useEffect, useRef, useState} from 'react';

const SearchResultPage = ({
  userIdSSR,
  query,
  seoConfig,
  filters,
}: {
  userIdSSR: number;
  query: string;
  seoConfig: object;
  filters: {
    posting: number;
    loc: string[];
    page: number;
    industry: string[];
  };
}) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, SEARCH);

  usePageLoad('search');

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  dispatch({
    type: 'AppContext/set_open_searchoverlay',
    payload: {search: false},
  });

  useValidateAuth(dispatch);

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: slugToTitle(query),
      item: '',
    },
  ];

  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);

  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);
  const hasFilterChanged = useRef(false);
  const [pageNumber, setPageNumber] = useState(filters?.page + 1);

  const [currentFilters, setCurrentFilters] = useState<{
    loc?: string[];
    minexp?: string;
    maxexp?: string;
    duration?: number;
    industry?: string[];
  }>({
    loc: filters.loc || [],
    minexp: (router.query.minexp as string) || '',
    maxexp: (router.query.maxexp as string) || '',
    duration: filters?.posting,
    industry: filters.industry,
  });

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  // Reset tracking when filter changes
  useEffect(() => {
    if (initialPageTracked.current) {
      hasFilterChanged.current = true;
    }
  }, [currentFilters]);

  useEffect(() => {
    if (
      hasTracked.current &&
      !hasPageNoChanged.current &&
      !hasFilterChanged.current
    )
      return;
    let actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';
    if (hasFilterChanged.current) {
      actionType = 'filter';
    }
    const sessionUserId = userIdSSR || userId || '-1';

    hasTracked.current = true;
    hasPageNoChanged.current = false;
    hasFilterChanged.current = false;
    TrackEventUBA('searchResultPageView', actionType, {
      ref: router.query.ref || '',
      pageName: 'SearchResultPage',
      pageNo: pageNumber,
      keyword:
        slugToTitle(query)?.replace(/(^\w{1})|(\s+\w{1})/g, letter =>
          letter.toUpperCase()
        ) || '',
      // filterExp: router.query.minexp
      //   ? `${router.query.minexp}-${router.query.maxexp}`
      //   : '',
      filterExp: currentFilters?.maxexp
        ? `${currentFilters.minexp}-${currentFilters.maxexp}`
        : '',
      filterDuration: currentFilters?.duration || '',
      filterLocation: currentFilters?.loc?.join(',') || '',
      filterIndustry: currentFilters?.industry?.join(',') || '',
      sessionUsername: `${sessionUserId}`,
    });
    initialPageTracked.current = true;
  }, [userId, pageNumber, currentFilters]);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout showbottom={true}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            <JobFeed
              jobFeedType={SEARCH}
              catOrTagId={query}
              catOrTagTitle={`Search for - ${slugToTitle(query)?.replace(
                /(^\w{1})|(\s+\w{1})/g,
                letter => letter.toUpperCase()
              )}`}
              showProfileCard={false}
              showFeatured={true}
              showLearning={true}
              preFilters={filters}
              setPageNumber={setPageNumber}
              onFiltersChange={setCurrentFilters}
            />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const posting = parseInt(context.query?.posting) || 0;
    const industry = context.query?.industry?.split(',') || [];
    const location = getLocationIds(context.query?.loc || '');
    const query = context.params?.query || context.query?.query;
    const slugUrl = context?.resolvedUrl || '';

    let userId = -1;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
        }
      }
    } catch (err) {}

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SEARCH},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const formatedQuerySlug: string = query
      ?.split('-')
      .join(' ')
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter: string) =>
        letter.toUpperCase()
      );

    console.log(
      '🚀 ~ file: [query].tsx:99 ~ store=> ~ formatedQuerySlug:',
      formatedQuerySlug
    );

    store.dispatch({type: 'job/jobFeed/set_initial_state'});

    const locationIds = location
      ? location?.split(',')?.map(v => parseInt(v))
      : null;
    const filters = {
      page: pageNo > 0 ? pageNo - 1 : 0,
      minexp: minExp,
      maxexp: maxExp,
      loc: locationIds,
      posting,
      query: formatedQuerySlug.replace(/\ jobs/gi, ''),
      industry,
    };

    const seoConfig = await loadSEO(
      SEARCH,
      getListSEOContent(formatedQuerySlug),
      getPageUrl(slugUrl, pageNo),
      true,
      pageNo
    );

    return {
      props: {
        userIdSSR: userId,
        query: formatedQuerySlug.replace(/\ jobs/gi, ''),
        seoConfig,
        filters,
      },
    };
  }
);

export default SearchResultPage;
